/**
 * Lernapp Data Models (Phase 1: Technische Spezifikation)
 *
 * Diese Datei definiert die neuen Datenstrukturen für den Umbau der Kursmodul-App
 * zur Lernapp mit der neuen Hierarchie: Kurs > Modul > Lektion > Lernbaustein > Meilenstein.
 *
 * Alle navigierbaren und zustandsbehafteten Einheiten verwenden stabile String-IDs
 * statt der bisherigen numerischen IDs.
 */

// ============================================================================
// IDENTIFIERS & BASE TYPES
// ============================================================================

/**
 * Stabile String-ID für navigierbare Einheiten
 * Format-Konventionen:
 * - Kurse: "course-001", "course-git", etc.
 * - Module: "module-git-basics", "module-git-workflow", etc.
 * - Lektionen: "lesson-git-basics-01", "lesson-git-workflow-02", etc.
 * - Lernbausteine: "block-git-basics-intro", "block-git-basics-practice", etc.
 * - Aufgaben: "task-git-setup", "task-first-commit", etc.
 */
export type StableId = string & { readonly __brand: 'StableId' };

/**
 * Erstelle eine validierte Stable-ID
 */
export function createStableId(id: string): StableId {
  if (!id || id.trim().length === 0) {
    throw new Error('Stable ID cannot be empty');
  }
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(id)) {
    throw new Error(`Invalid Stable ID format: "${id}". Use lowercase alphanumeric with hyphens.`);
  }
  return id as StableId;
}

/**
 * Completion status for learning units
 */
export type CompletionStatus = 'not-started' | 'in-progress' | 'completed' | 'skipped';

/**
 * Task status in the learning context
 */
export type TaskStatus = 'open' | 'in-work' | 'completed' | 'skipped';

/**
 * Block type – defined content template types
 */
export type LernbausteinBlockType =
  | 'text'
  | 'image'
  | 'image-with-text'
  | 'gallery'
  | 'youtube-video'
  | 'quiz'
  | 'task-hint'
  | 'checkpoint'
  | 'learning-goals'
  | 'interactive-component'
  | 'table'
  | 'callout'
  | 'warning'
  | 'download'
  | 'exercise'
  | 'code-snippet';

// ============================================================================
// CORE LEARNING HIERARCHY
// ============================================================================

/**
 * Kurs – oberste Ebene, umfasst mehrere Module
 */
export interface Kurs {
  id: StableId;
  title: string;
  description?: string;
  language: 'de' | 'en';
  moduleIds: StableId[]; // Ordered list of module IDs
  createdAt: Date;
  updatedAt: Date;
  version: number;
}

/**
 * Modul – verfolgt einen eigenständigen Lernerfolg, endet mit Meilenstein
 */
export interface Modul {
  id: StableId;
  courseId: StableId;
  title: string;
  description?: string;
  learningGoal: string; // What learner can do after completing this module
  order: number; // Position in course
  lectionIds: StableId[]; // Ordered list of lesson IDs
  milestoneId: StableId; // Single milestone at end of module
  isRequired: boolean; // Is this module mandatory?
  prerequisites?: StableId[]; // IDs of modules that must be completed first
  createdAt: Date;
  updatedAt: Date;
  version: number;
}

/**
 * Lektion – entspricht bisherigem "Step", enthält mehrere Lernbausteine
 */
export interface Lektion {
  id: StableId;
  moduleId: StableId;
  title: string;
  description?: string;
  order: number; // Position in module
  lernbausteinIds: StableId[]; // Ordered list of learning block IDs
  isRequired: boolean; // Can this lesson be skipped?
  completionRule?: 'all' | 'any'; // How many blocks must be completed?
  createdAt: Date;
  updatedAt: Date;
  version: number;
}

/**
 * Lernbaustein – kleinste navigierbare Lerneinheit
 */
export interface Lernbaustein {
  id: StableId;
  lectionId: StableId;
  title?: string;
  order: number; // Position in lesson
  blocks: LernbausteinBlock[]; // Content blocks
  isRequired: boolean;
  completionRule: LernbausteinCompletionRule;
  requiresScroll?: boolean; // Does "Next" button appear only after scroll?
  createdAt: Date;
  updatedAt: Date;
  version: number;
}

/**
 * Rule for completing a learning block
 */
export type LernbausteinCompletionRule =
  | { type: 'click-next' } // Completed by clicking "Next"
  | { type: 'scroll-end' } // Completed after scrolling to end
  | { type: 'quiz-attempt' } // Completed after one quiz attempt
  | { type: 'interactive'; condition: string } // Custom condition
  | { type: 'all-tasks-completed' }; // All task hints must open tasks

/**
 * Content block within a learning unit
 */
export interface LernbausteinBlock {
  id: StableId;
  type: LernbausteinBlockType;
  contentRef?: string; // Reference to external content (markdown path, etc.)
  metadata?: Record<string, unknown>; // Type-specific metadata
}

/**
 * Meilenstein – concludes a module, consists of task + summary + self-assessment
 */
export interface Meilenstein {
  id: StableId;
  moduleId: StableId;
  title: string;
  summary: string; // Summary of module learning
  taskId: StableId; // Final practical task
  selfAssessmentQuestions?: string[]; // Optional self-reflection questions
  createdAt: Date;
  updatedAt: Date;
  version: number;
}

// ============================================================================
// TASKS & ASSIGNMENTS
// ============================================================================

/**
 * Task origin – where the task was introduced
 */
export interface TaskOrigin {
  moduleId: StableId;
  lectionId: StableId;
  lernbausteinId: StableId;
}

/**
 * Task type
 */
export type TaskType = 'required' | 'optional' | 'milestone';

/**
 * Task – assignment for learner to complete
 */
export interface Task {
  id: StableId;
  title: string;
  description: string;
  taskType: TaskType;
  origin: TaskOrigin; // Where was this task introduced?
  dueDate?: Date; // Optional deadline
  hints?: string[]; // Helpful hints
  successCriteria?: string[]; // How to know if completed?
  createdAt: Date;
  updatedAt: Date;
  version: number;
}

/**
 * Task instance in learner state – tracks status of a task
 */
export interface TaskInstance {
  taskId: StableId;
  status: TaskStatus;
  completionSource?: 'user' | 'manual-lesson';
  startedAt?: Date;
  completedAt?: Date;
  notes?: string; // Learner's notes
}

// ============================================================================
// PREREQUISITES & ADAPTIVE PATHS
// ============================================================================

/**
 * Prerequisite check – determines if learner can access certain content
 */
export interface PrerequisiteCheck {
  id: StableId;
  question: string; // "Do you have a GitHub account?"
  type: 'yes-no' | 'multiple-choice' | 'skill-assessment';
  options?: string[]; // For multiple-choice
  affectsPath?: {
    ifYes?: { skipLectionIds?: StableId[]; recommendModuleIds?: StableId[] };
    ifNo?: { recommendLectionIds?: StableId[]; blockModuleIds?: StableId[] };
  };
}

// ============================================================================
// GLOSSARY
// ============================================================================

/**
 * Glossary entry – centralized term definitions
 */
export interface GlossaryCourseReference {
  courseId: StableId;
  moduleId: StableId;
}

export interface GlossaryEntry {
  id: StableId;
  term: string;
  shortDefinition: string; // For tooltips
  fullDefinition: string; // For wiki-style page
  examples?: string[];
  relatedTerms?: StableId[];
  sources?: { title: string; url: string }[];
  moduleIds?: StableId[]; // Optional: which modules use this term?
  courseRefs?: GlossaryCourseReference[]; // Optional for legacy snapshots without course refs
  language: 'de' | 'en';
}

/**
 * Term reference in content – automatic or manually confirmed
 */
export interface TermReference {
  glossaryEntryId: StableId;
  context: string; // Where in the content is this term used?
  isConfirmed: boolean; // Was this manual reference confirmed by editor?
}

// ============================================================================
// LEARNER STATE
// ============================================================================

/**
 * Learner progress snapshot for a course
 * Stored in localStorage with key: `lernapp_progress_{courseId}`
 */
export interface LearnerCourseProgress {
  courseId: StableId;
  userId?: string; // Optional, for future sync
  version: number; // Schema version for migration
  startedAt: Date;
  lastAccessedAt: Date;
  moduleProgress: {
    moduleId: StableId;
    status: CompletionStatus;
    statusBeforeSkip?: Exclude<CompletionStatus, 'skipped'>;
    lectionProgress: {
      lectionId: StableId;
      status: CompletionStatus;
      completionSource?: 'automatic' | 'manual';
      lernbausteinProgress: {
        lernbausteinId: StableId;
        status: CompletionStatus;
        completionSource?: 'automatic' | 'manual';
        viewedAt?: Date;
      }[];
    }[];
    milestoneStatus?: CompletionStatus;
    milestoneCompletedAt?: Date;
  }[];
  tasks: TaskInstance[];
  bookmarks?: StableId[]; // Bookmarked lernbausteine
  notes?: { lernbausteinId: StableId; content: string }[];
}

/**
 * Global learner state
 */
export interface LearnerState {
  courseProgressMap: Map<StableId, LearnerCourseProgress>;
  preferences: {
    language: 'de' | 'en';
    sidebarCollapsed: boolean;
    lastOpenedCourseId?: StableId;
    overviewOnboardingDecided?: boolean;
  };
}

// ============================================================================
// SCHEMA METADATA
// ============================================================================

/**
 * Content schema version info for migration tracking
 */
export interface SchemaVersion {
  major: number;
  minor: number;
  patch: number;
  migratedFrom?: SchemaVersion; // What version was this migrated from?
}

/**
 * Config for content validation
 */
export interface ContentValidationConfig {
  requiredBlockTypes: LernbausteinBlockType[];
  allowedMaxLernbausteinBlockCount: number; // Max blocks per lernbaustein
  allowedMaxLektionBlockCount: number; // Max lernbausteine per lektion
  stringIdPattern: RegExp; // Pattern for validating stable IDs
}
